/**
 * Single source for Use Cases hub, nav dropdown, and /use-case/:slug placeholders.
 */

import { SOLUTION_HOW_IT_WORKS } from '../solutions/solutionLandingPages';

export const USE_CASES_HUB_PATH = '/use-cases';

/** How it works on /use-cases — same as solution landings / homepage. */
export const USE_CASES_PAGE_HOW_IT_WORKS = { ...SOLUTION_HOW_IT_WORKS };

export const USE_CASE_NAV_TOP = {
    to: '/use-cases',
    title: 'All use cases',
    description: 'Find the workflow you want to automate',
};

/** Grouped nav + hub sections (same cards copy). */
export const USE_CASE_SECTIONS = [
    {
        key: 'reporting',
        label: 'Reporting',
        items: [
            {
                slug: 'client-reporting',
                navTitle: 'Client reporting',
                description: 'Client reports that update themselves',
                path: '/use-case/client-reporting',
            },
            {
                slug: 'automated-reporting',
                navTitle: 'Automated reporting',
                description: 'Reports stay current without manual exports',
                path: '/use-case/automated-reporting',
            },
            {
                slug: 'marketing-dashboard',
                navTitle: 'Marketing dashboards',
                description: 'Ad and campaign data in one place',
                path: '/use-case/marketing-dashboard',
            },
            {
                slug: 'weekly-report-automation',
                navTitle: 'Weekly report automation',
                description: 'No more rebuilding the same report every week',
                path: '/use-case/weekly-report-automation',
            },
        ],
    },
    {
        key: 'data-automation',
        label: 'Data Automation',
        items: [
            {
                slug: 'stop-exporting-data',
                navTitle: 'Stop exporting data',
                description: 'Replace CSV exports with automatic updates',
                path: '/use-case/stop-exporting-data',
            },
            {
                slug: 'sync-data-between-apps',
                navTitle: 'Sync apps together',
                description: 'Keep data moving between your tools',
                path: '/use-case/sync-data-between-apps',
            },
            {
                slug: 'automate-spreadsheets',
                navTitle: 'Automate spreadsheets',
                description: 'Keep sheets updated without copy/paste',
                path: '/use-case/automate-spreadsheets',
            },
        ],
    },
    {
        key: 'business-workflows',
        label: 'Business Workflows',
        items: [
            {
                slug: 'orders-to-accounting',
                navTitle: 'Orders to accounting',
                description: 'Sales and orders flow into accounting',
                path: '/use-case/orders-to-accounting',
            },
            {
                slug: 'sales-to-dashboard',
                navTitle: 'Sales to dashboard',
                description: 'Sales data stays ready for reporting',
                path: '/use-case/sales-to-dashboard',
            },
            {
                slug: 'crm-to-spreadsheet',
                navTitle: 'CRM to spreadsheet',
                description: 'Customer data stays synced automatically',
                path: '/use-case/crm-to-spreadsheet',
            },
        ],
    },
];

/** Must match pages defined in useCaseDetailContent.js */
export { USE_CASE_SLUGS } from './useCaseDetailContent';

/** Featured row on /use-cases */
export const USE_CASES_FEATURED = [
    {
        slug: 'client-reporting',
        title: 'Client reporting',
        description: 'Client reports stay up to date automatically—no Friday rebuilds.',
        path: '/use-case/client-reporting',
        accent: 'orange',
    },
    {
        slug: 'automated-reporting',
        title: 'Automated reporting',
        description: 'Reports update themselves from the tools you already use.',
        path: '/use-case/automated-reporting',
        accent: 'sky',
    },
    {
        slug: 'stop-exporting-data',
        title: 'Stop exporting data',
        description: 'Replace CSV exports with data that updates automatically.',
        path: '/use-case/stop-exporting-data',
        accent: 'orange',
    },
    {
        slug: 'automate-spreadsheets',
        title: 'Automate spreadsheets',
        description: 'Keep Google Sheets and Excel updated without copy/paste.',
        path: '/use-case/automate-spreadsheets',
        accent: 'sky',
    },
];

export const USE_CASES_HUB_HERO = {
    eyebrow: 'Use cases',
    headlineLine1: 'What are you still doing by hand?',
    headlineAccent: '',
    lead: 'Pick the workflow you want to stop rebuilding, exporting, or copy/pasting every week.',
    primaryCta: 'Set up your first sync',
    secondaryCta: 'Browse workflows',
    secondaryToHash: '#use-cases-featured',
    microcopy: 'Most setups take under 5 minutes. Works with 100+ tools.',
    trustChips: ['7-day free trial', 'No credit card', 'Clear pricing'],
};

export const USE_CASES_POPULAR_PILLS = [
    { label: 'Shopify → QuickBooks', to: '/shopify-to-quickbooks' },
    { label: 'Shopify → Google Sheets', to: '/integrations' },
    { label: 'Facebook Ads → Dashboard', to: '/solutions/marketing-teams' },
    { label: 'Google Ads → Google Sheets', to: '/integrations' },
    { label: 'Stripe → QuickBooks', to: '/register-company' },
    { label: 'HubSpot → Google Sheets', to: '/integrations' },
];

/** Full page content for /use-case/:slug — title, SEO, hero, workflows, screenshots, CTAs */
export { USE_CASE_PAGE_CONFIG } from './useCaseDetailContent';
